import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { listPostsByAuthor } from "@/lib/blog.functions";
import { businessNode, canonical, SITE_URL } from "@/lib/seo";
import { ogImageMeta } from "@/lib/og-image";
import { Phone, MessageSquare, Award, Radio, Quote, ExternalLink } from "lucide-react";

const TITLE = "Tim Forstie | Owner, Durafoam Roofing — Phoenix Foam Roofing Since 1989";
const DESCRIPTION =
  "Tim Forstie is co-owner of Durafoam Roofing, the family-owned Phoenix foam roofing company founded by his father Steve in 1989. Rosie on the House's go-to foam roofing expert.";
const PATH = "/tim-forstie";
const URL = canonical(PATH);
const PERSON_ID = `${SITE_URL}/tim-forstie#person`;

const rosieEpisodes = [
  {
    title: "A Guide to Foam Roofing with Durafoam Roofing",
    date: "June 2026 · Rosie on the House",
    href: "https://rosieonthehouse.com/podcast/on-the-house-hour-a-guide-to-foam-roofing-with-durafoam-roofing/",
  },
  {
    title: "The Particulars of Foam Roofs with Tim Forstie of Durafoam Roofing",
    date: "May 2024 · Rosie on the House",
    href: "https://rosieonthehouse.com/podcast/on-the-house-hour-the-particulars-of-foam-roofs-with-tim-forstie-of-durafoam-roofing/",
  },
];

const timQuotes: Array<{ quote: string; context: string }> = [
  {
    quote: "The primary reason to use foam is the insulation factor it provides.",
    context: "on why foam outperforms other roofing materials",
  },
  {
    quote:
      "Many of the old roofs we do can be recovered with foam. We clean and prepare the existing membrane and then install the Durafoam system over the top. The process saves on costs for the homeowner and helps the environment by keeping old roofing materials out of our landfills.",
    context: "on foam-over-existing-roof installs",
  },
  {
    quote:
      "We recommend that homeowners have their roofs inspected annually to catch potential problems before they can cause costly repairs.",
    context: "on roof maintenance",
  },
];

const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Tim Forstie",
  alternateName: "Timothy Forstie",
  jobTitle: "Owner",
  url: URL,
  worksFor: { "@id": `${SITE_URL}/#business` },
  description:
    "Tim Forstie is co-owner of Durafoam Roofing, the family-owned Phoenix roofing company founded by his father Steve Forstie in 1989. Tim has been on Phoenix rooftops since 1989 and is Rosie on the House's recurring foam-roofing expert.",
  knowsAbout: [
    "Foam roofing",
    "Spray polyurethane foam roofing",
    "Roof recoating",
    "Roof leak repair",
    "Tile roofing",
    "Phoenix roofing",
  ],
};

const profilePageSchema = {
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  "@id": `${URL}#profilepage`,
  url: URL,
  name: TITLE,
  description: DESCRIPTION,
  mainEntity: { "@id": PERSON_ID },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: canonical("/") },
    { "@type": "ListItem", position: 2, name: "About", item: canonical("/about-us") },
    { "@type": "ListItem", position: 3, name: "Tim Forstie", item: URL },
  ],
};

export const Route = createFileRoute("/tim-forstie")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["blog", "author", "Tim Forstie"],
      queryFn: () => listPostsByAuthor({ data: { author: "Tim Forstie" } }),
    }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "profile" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImageMeta("tim-forstie"),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(businessNode({ url: PATH })) },
      { type: "application/ld+json", children: JSON.stringify(personSchema) },
      { type: "application/ld+json", children: JSON.stringify(profilePageSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
    ],
  }),
  component: TimForstiePage,
});

function TimForstiePage() {
  const fn = useServerFn(listPostsByAuthor);
  const { data: posts } = useSuspenseQuery({
    queryKey: ["blog", "author", "Tim Forstie"],
    queryFn: () => fn({ data: { author: "Tim Forstie" } }),
  });

  return (
    <>
      {/* Hero */}
      <section className="relative bg-primary text-white">
        <Container className="py-16 lg:py-24">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85">
            Owner · Durafoam Roofing · Phoenix, AZ
          </p>
          <h1 className="mt-3 max-w-4xl text-3xl font-bold leading-[1.15] text-white sm:text-4xl lg:text-5xl">
            Tim Forstie — on Phoenix rooftops since 1989.
          </h1>
          <p className="mt-5 max-w-3xl text-base text-white/90 sm:text-lg">
            Tim is co-owner of Durafoam Roofing, the family-owned Phoenix foam roofing company founded by his father Steve in 1989. He's the recurring foam-roofing expert on Rosie on the House and the voice behind the interview-style articles on this site.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule a Free Inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </ButtonLink>
            <ButtonLink
              href="sms:+14809415373"
              variant="ghost-light"
              size="lg"
              className="sm:hidden"
            >
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Text Us
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* Family history */}
      <section className="bg-background py-14 lg:py-16">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-primary">A family business — same name, same standards.</h2>
            <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-foreground/85">
              <p>
                Durafoam Roofing was founded in 1989 by Steve Forstie with one principle: produce the highest quality roof possible at an affordable cost. Steve's sons Tim and Curtis grew up on Phoenix rooftops learning the trade from him, and took over running the company as co-owners.
              </p>
              <p>
                That continuity is why homeowners who called Durafoam in the '90s still call today: it's the same family, the same craftsmanship, and the same repair-first approach — no salespeople, no upsells, no pressure.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Media / Rosie */}
      <section className="bg-[var(--surface)] py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 text-accent">
              <Award className="h-5 w-5" aria-hidden="true" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                Media appearances
              </span>
            </div>
            <h2 className="mt-3 text-3xl font-bold text-primary">
              Rosie on the House's go-to for foam roofing.
            </h2>
            <p className="mt-4 text-[17px] leading-relaxed text-foreground/85">
              Rosie on the House — Arizona's longest-running home-improvement radio show and podcast — has certified Durafoam as its foam-roofing partner since 1996. Tim has appeared on the show multiple times to walk Arizona homeowners through foam roofing, maintenance, and monsoon-season repairs.
            </p>

            <div className="mt-8 space-y-4">
              {rosieEpisodes.map((ep) => (
                <a
                  key={ep.href}
                  href={ep.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 rounded-xl border border-border bg-white p-5 transition-shadow hover:shadow-md"
                >
                  <span className="mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-md bg-[var(--surface)] text-accent">
                    <Radio className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div className="flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
                      {ep.date}
                    </p>
                    <p className="mt-1 font-semibold text-primary">{ep.title}</p>
                  </div>
                  <ExternalLink className="mt-1 h-4 w-4 flex-shrink-0 text-[var(--muted-foreground)]" aria-hidden="true" />
                </a>
              ))}
            </div>

            <p className="mt-6 text-sm text-[var(--muted-foreground)]">
              See every media appearance on the{" "}
              <Link to="/rosie-on-the-house" className="text-accent underline">
                Rosie on the House hub page
              </Link>
              .
            </p>
          </div>
        </Container>
      </section>

      {/* In Tim's words */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-primary">In Tim's words</h2>
            <div className="mt-8 space-y-5">
              {timQuotes.map((q, i) => (
                <figure key={i} className="rounded-xl border-l-4 border-accent bg-white p-6 shadow-sm">
                  <Quote className="h-6 w-6 text-accent/40" aria-hidden="true" />
                  <blockquote className="mt-3 text-[17px] leading-relaxed text-foreground/90 italic">
                    "{q.quote}"
                  </blockquote>
                  <figcaption className="mt-3 text-sm text-[var(--muted-foreground)]">
                    — Tim Forstie, {q.context}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Articles by Tim */}
      <section className="bg-[var(--surface)] py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold text-primary">Articles by Tim</h2>
            <p className="mt-3 text-[var(--muted-foreground)]">
              Interview-style Q&amp;A articles written directly from Tim's answers on Phoenix foam roofing.
            </p>

            {posts.length === 0 ? (
              <p className="mt-8 rounded-xl border border-border bg-white p-8 text-center text-[var(--muted-foreground)]">
                Tim's next article is on the way. In the meantime, browse the{" "}
                <Link to="/blog" className="text-accent underline">full blog</Link>.
              </p>
            ) : (
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {posts.map((p) => (
                  <BlogPostCard key={p.id} post={p} />
                ))}
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-primary py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold text-white sm:text-3xl">
            Want Tim's team to take a look at your roof?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/85">
            Free, no-pressure inspection. Repair-first — we won't recommend a replacement your roof doesn't need.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule a Free Inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}
